import { useState, useCallback } from 'react';
import { Project, MarkupState, ToolType, SymbolType, SystemType } from '@shared/types/tsk';
import { getAllProjects, saveProject as dbSaveProject, deleteProject as dbDeleteProject } from '../utils/storage';

export const useTradeSketchState = (initialProjects: Project[]) => {
  const [projects, setProjects] = useState<Project[]>(initialProjects);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [markup, setMarkup] = useState<MarkupState>({ lines: [], symbols: [] });
  const [history, setHistory] = useState<MarkupState[]>([]);
  const [redoStack, setRedoStack] = useState<MarkupState[]>([]);
  const [activeTool, setActiveTool] = useState<ToolType>(ToolType.PAN);
  const [activeSymbol, setActiveSymbol] = useState<SymbolType>(SymbolType.WC);
  const [activeSystem, setActiveSystem] = useState<SystemType>(SystemType.DRAINAGE);

  const handleSave = useCallback(async () => {
    if (!currentProjectId) return;
    const projectToSave = projects.find(p => p.id === currentProjectId);
    if (projectToSave) {
      const updatedProject = {
        ...projectToSave,
        markup,
        lastModified: Date.now()
      };
      await dbSaveProject(updatedProject);
      setProjects(prev => prev.map(p => p.id === currentProjectId ? updatedProject : p));
    }
  }, [currentProjectId, projects, markup]);

  const updateProjectDetails = async (updatedFields: Partial<Project>) => {
    if (!currentProjectId) return;
    const projectToUpdate = projects.find(p => p.id === currentProjectId);
    if (projectToUpdate) {
      const updatedProject = { ...projectToUpdate, ...updatedFields, lastModified: Date.now() };
      await dbSaveProject(updatedProject);
      setProjects(prev => prev.map(p => p.id === currentProjectId ? updatedProject : p));
    }
  };

  const createNewProject = async (imageData: string) => {
    const id = Date.now().toString();
    const newProject: Project = {
      id,
      name: `Sketch ${id}`,
      createdAt: Date.now(),
      lastModified: Date.now(),
      image: imageData,
      thumbnail: imageData,
      markup: { lines: [], symbols: [] }
    };
    try {
      await dbSaveProject(newProject);
      const nextProjects = [newProject, ...projects];
      setProjects(nextProjects);
      setCurrentProjectId(id);
      setImage(imageData);
      setMarkup(newProject.markup || { lines: [], symbols: [] });
      setHistory([newProject.markup || { lines: [], symbols: [] }]);
      setRedoStack([]);
    } catch (err) {
      console.error(err);
    }
  };

  const loadProject = (id: string, currentProjects: Project[] = projects) => {
    const p = currentProjects.find(item => item.id === id);
    if (p) {
      setCurrentProjectId(id);
      setImage(p.image);
      setMarkup(p.markup || { lines: [], symbols: [] });
      setHistory([p.markup || { lines: [], symbols: [] }]);
      setRedoStack([]);
    }
  };

  const deleteProject = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Delete this sketch permanently?')) {
      try {
        await dbDeleteProject(id);
        const next = projects.filter(p => p.id !== id);
        setProjects(next);
        if (currentProjectId === id) {
          setCurrentProjectId(null);
          setImage(null);
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const updateMarkup = useCallback((newMarkup: MarkupState | ((prev: MarkupState) => MarkupState), pushToHistory = true) => {
    setMarkup(prev => {
      const next = typeof newMarkup === 'function' ? newMarkup(prev) : newMarkup;
      if (pushToHistory) {
        setHistory(h => {
          const lastHead = h[h.length - 1];
          if (JSON.stringify(lastHead) === JSON.stringify(next)) return h;
          return [...h, next].slice(-50);
        });
        setRedoStack([]);
      }
      return next;
    });
  }, []);

  const handleUndo = useCallback(() => {
    if (history.length <= 1) return;
    const newHistory = [...history];
    const current = newHistory.pop()!;
    const prev = newHistory[newHistory.length - 1];
    setHistory(newHistory);
    setRedoStack(r => [...r, current]);
    setMarkup(prev);
  }, [history]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const newRedo = [...redoStack];
    const next = newRedo.pop()!;
    setRedoStack(newRedo);
    setHistory(h => [...h, next]);
    setMarkup(next);
  }, [redoStack]);

  return {
    projects, setProjects,
    currentProjectId, setCurrentProjectId,
    image, setImage,
    markup, setMarkup,
    history, setHistory,
    redoStack, setRedoStack,
    activeTool, setActiveTool,
    activeSymbol, setActiveSymbol,
    activeSystem, setActiveSystem,
    handleSave, updateProjectDetails, createNewProject, loadProject, deleteProject,
    updateMarkup, handleUndo, handleRedo
  };
};
